import type { VantageToFicoRow } from './GlobalData'

/** Scoring model behind a stated credit score (stored in credit_score_source). */
export enum CreditScoreModel {
  Fico = 'fico',
  Vantage = 'vantage',
}

export const CREDIT_SCORE_MODEL_LABELS: Record<CreditScoreModel, string> = {
  [CreditScoreModel.Fico]: 'FICO',
  [CreditScoreModel.Vantage]: 'VantageScore (Credit Karma)',
}

export const VANTAGE_FICO_NOTE =
  'Partner guidelines use FICO thresholds. Partner Eligibility shows the FICO this VantageScore is compared as.'

export function vantageToFicoTableError(
  rows: readonly VantageToFicoRow[]
): string | null {
  const inRange = (n: unknown) =>
    Number.isInteger(n) && (n as number) >= 300 && (n as number) <= 850
  if (rows.some((row) => !inRange(row?.fico) || !inRange(row?.vantage))) {
    return 'every fico and vantage must be a whole number between 300 and 850'
  }
  const byFico = [...rows].sort((a, b) => a.fico - b.fico)
  for (let i = 1; i < byFico.length; i++) {
    const [prev, row] = [byFico[i - 1], byFico[i]]
    if (row.fico === prev.fico) return `fico ${row.fico} appears more than once`
    if (row.vantage < prev.vantage) {
      return `vantage falls from ${prev.vantage} to ${row.vantage} between fico ${prev.fico} and ${row.fico}`
    }
  }
  return null
}

const FICO_MINIMUM = 300

/**
 * The equivalent classic FICO for a VantageScore, read off the FICO→Vantage table in reverse.
 * Several FICOs share one VantageScore and some VantageScores never appear, so the lookup takes
 * the lowest FICO at the nearest VantageScore at or below the input. A
 * score under the table's floor has no known equivalent and is reported as the FICO minimum,
 * which no tier can accept; one over its
 * ceiling as the highest.
 */
export function vantageToFico(
  vantage: number,
  rows: readonly VantageToFicoRow[]
): number | undefined {
  const lowestFicoByVantage = new Map<number, number>()
  rows.forEach(({ fico, vantage: v }) => {
    if (!Number.isFinite(fico) || !Number.isFinite(v)) return
    const current = lowestFicoByVantage.get(v)
    if (current === undefined || fico < current)
      lowestFicoByVantage.set(v, fico)
  })
  if (lowestFicoByVantage.size === 0) return undefined

  const breakpoints = [...lowestFicoByVantage.entries()].sort(
    (a, b) => a[0] - b[0]
  )
  const [floorVantage, floorFico] = breakpoints[0]
  if (vantage < floorVantage) return FICO_MINIMUM

  let fico = floorFico
  for (const [v, f] of breakpoints) {
    if (v > vantage) break
    fico = f
  }
  return fico
}
